// TODO: Preload this in the head tag
import { GetCurrentUser200 } from '@platform/client/schemas';
import mixpanel, { Mixpanel } from 'mixpanel-browser';
import { useCallback, useEffect } from 'react';
import { useConsent } from './useConsent';

export const MIXPANEL_ENABLED =
  !import.meta.env.SSR &&
  !!import.meta.env.VITE_MIXPANEL_TOKEN?.length &&
  import.meta.env.VITE_ANALYTICS_ENABLED;

type MixpanelProperties = Parameters<Mixpanel['track']>[1];

/**
 *  https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method#client-side-tracking
 */

export const initMixpanel = () => {
  if (!MIXPANEL_ENABLED) return false;
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
    debug: import.meta.env.VITE_ENVIRONMENT === 'development',
    track_pageview: 'url-with-path-and-query-string',
    persistence: 'localStorage',
  });
  mixpanel.register({
    // Indicates that the user is using this application (the new "Platform" app) instead of
    // the legacy Hapi app
    from_new_platform: true,
    // TODO: App version?
  });
  return true;
};

/** confirms mixpanel is enabled and user has consented then identifies the user */
export const useIdentifyMixpanelUser = (user?: GetCurrentUser200) => {
  const { consent } = useConsent();
  useEffect(() => {
    if (!MIXPANEL_ENABLED || !consent.analytics || !consent.initialized || !user) {
      return;
    }
    mixpanel.identify(user.userId.toString());
  }, [user, consent.analytics, consent.initialized]);
};

/**
 * Attaches a Mixpanel track_links() call for a specific event to an element via the returned ref. The track_links
 * will fire when the element is clicked
 * @param eventName String name of the event. If the empty string this function does not attach the listener and just returns the (useless) ref. This is effectively a no-op for cases where we don't need tracking.
 * @param properties Optional properties to attach to the event. The Mixpanel docs claim this can be a function that returns properties when the link is clicked
 * @returns The ref to attach to the element that should trigger tracking
 */
export const useMixpanelTrackLink = <T extends HTMLElement = HTMLDivElement>(
  eventName: string,
  properties?: MixpanelProperties,
) => {
  const { consent } = useConsent();
  const setRef = useCallback(
    (el: T) => {
      if (!MIXPANEL_ENABLED || !consent.analytics || !consent.initialized) {
        return;
      }

      if (eventName.length && el) {
        mixpanel.track_links(el, eventName, properties);
      }
    },
    [eventName, properties, consent.analytics, consent.initialized],
  );

  return setRef;
};
